import React, { useEffect, useLayoutEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

const NAV_ITEMS = [
  {
    to: '/',
    label: 'home',
    icon: <><path d="M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" /><path d="M9.5 21v-7h5v7" /></>
  },
  {
    to: '/photography',
    label: 'photography',
    icon: <><path d="M3 7.5h4L9 5h6l2 2.5h4v12H3z" /><circle cx="12" cy="13" r="3.6" /></>
  },
  {
    to: '/projects',
    label: 'projects',
    icon: <><path d="m4 17 6-5.5L4 6" /><path d="M12 18.5h8" /></>
  },
  {
    to: '/map',
    label: 'map',
    icon: <><path d="M2 6.5 8.5 3l7 3.5L22 3v14.5L15.5 21l-7-3.5L2 21z" /><path d="M8.5 3v14.5M15.5 6.5V21" /></>
  },
  {
    to: '/stats',
    label: 'stats',
    icon: <path d="M6 20v-6M12 20V4M18 20v-10" />
  },
  {
    to: '/aurora',
    label: 'aurora',
    icon: <><path d="M2 10c3-5 5.5-6 8.5-3s5.5 3 11.5-4" /><path d="M2 15c3-5 5.5-6 8.5-3s5.5 3 11.5-4" /><path d="M2 20c3-5 5.5-6 8.5-3s5.5 3 11.5-4" /></>
  },
  {
    to: '/moon',
    label: 'moon',
    icon: <path d="M20.5 13.5A8.5 8.5 0 1 1 10.5 3.5a6.8 6.8 0 0 0 10 10z" />
  },
  {
    to: '/planets',
    label: 'planets',
    icon: <><circle cx="12" cy="12" r="5" /><ellipse cx="12" cy="12" rx="10.5" ry="3.2" transform="rotate(-22 12 12)" /></>
  },
  {
    to: '/game',
    label: 'game',
    icon: <><rect x="2" y="7" width="20" height="11" rx="3" /><path d="M7 10.5v4M5 12.5h4M15.5 11h.01M18 14h.01" /></>
  },
  {
    to: '/connections',
    label: 'connections',
    icon: <><rect x="3" y="3" width="7.5" height="7.5" /><rect x="13.5" y="3" width="7.5" height="7.5" /><rect x="3" y="13.5" width="7.5" height="7.5" /><rect x="13.5" y="13.5" width="7.5" height="7.5" /></>
  }
];

const Icon = ({ children }) => (
  <svg className="rail-icon" viewBox="0 0 24 24" aria-hidden="true">{children}</svg>
);

const PIN_KEY = 'sidebar-pinned';

function readPinned() {
  try {
    return window.localStorage.getItem(PIN_KEY) === '1';
  } catch (error) {
    return false;
  }
}

function writePinned(pinned) {
  try {
    window.localStorage.setItem(PIN_KEY, pinned ? '1' : '0');
  } catch (error) {
    // Private windows can refuse storage. The sidebar still works this visit.
  }
}

// Desktop: a slim rail that opens on hover and floats over the page, so looking
// up a label never reflows what you're reading. The toggle pins it open, which
// does push the page over -- that's the point of pinning it.
// Phone: there's no width to spare, so the same toggle slides it in as a drawer.
function Sidebar() {
  const [pinned, setPinned] = useState(readPinned);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { pathname } = useLocation();

  // On <html> because the page, the rail and fixed full-screen views like the
  // game all size themselves from --rail-w.
  useLayoutEffect(() => {
    document.documentElement.toggleAttribute('data-rail-pinned', pinned);
  }, [pinned]);

  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setDrawerOpen(false);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [drawerOpen]);

  const toggle = () => {
    if (window.matchMedia('(max-width: 768px)').matches) {
      setDrawerOpen((open) => !open);
      return;
    }
    const next = !pinned;
    setPinned(next);
    writePinned(next);
  };

  return (
    <>
      <button
        type="button"
        className="rail-toggle"
        onClick={toggle}
        aria-label="Toggle navigation"
        aria-expanded={drawerOpen || pinned}
      >
        <Icon><path d="M4 7h16M4 12h16M4 17h16" /></Icon>
      </button>

      <div
        className={`rail-scrim${drawerOpen ? ' open' : ''}`}
        onClick={() => setDrawerOpen(false)}
      />

      <nav className={`rail${drawerOpen ? ' open' : ''}`} aria-label="Main">
        <ul>
          {NAV_ITEMS.map(({ to, label, icon }) => (
            <li key={to}>
              <NavLink to={to} end={to === '/'} onClick={() => setDrawerOpen(false)}>
                <Icon>{icon}</Icon>
                <span className="rail-label">{label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </>
  );
}

export default Sidebar;
